/**
 * The two rules Phase 7b adds to a gallery card, in a file with no imports.
 *
 * `entries.ts` reaches `./calendar` without an extension, which Node's test
 * loader cannot resolve, so anything in it is untestable offline — the same
 * constraint `filmRank.ts`, `exhibitionText.ts` and `reach.ts` already exist
 * to work around. Both of these decide something a screenshot would have to
 * be read to check, so both belong here.
 */

/** Which timestamp a card may print a clock from, or null for none.
 *
 *  `showing_count` counts performances from Phase 7b on, and an exhibition
 *  has none: its run marker and its vernissage are annotations. So a count
 *  of 1 is an ordinary one-off and `next_showing` is its time, and a count of
 *  0 on a row that has a `next_opening` is a party — an `opening` entry, or
 *  an exhibition whose run is one day. Anything above 1 has no single time
 *  to state, and a card that named the first of several would be claiming it
 *  was *the* one.
 */
export function clockAt(
  showingCount: number | null,
  nextShowing: string | null,
  nextOpening: string | null,
): string | null {
  if (showingCount === 1) return nextShowing
  if ((showingCount ?? 0) === 0) return nextOpening
  return null
}

/** Artists for the line under an exhibition's title: three, then "+N".
 *
 *  A group show lists eleven names and a card is not a wall label. Trimmed
 *  and emptied here rather than at the two call sites, because a trailing
 *  ", " from a source that printed one would otherwise be a name. */
export function artistNames(names: readonly string[]): string | null {
  const clean = names.map((n) => n.trim()).filter(Boolean)
  if (!clean.length) return null
  return clean.length <= 3
    ? clean.join(', ')
    : `${clean.slice(0, 3).join(', ')} +${clean.length - 3}`
}
